import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Radar, Send, Loader2, Users, MapPin, Trophy, History, Search as SearchIcon, Sparkles } from 'lucide-react';
import { searchAPI } from '../services/api';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HISTORY_STORAGE_KEY = 'ai_discover_history';

const AIDiscover = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentResults, setCurrentResults] = useState(null);
  const [currentExplanation, setCurrentExplanation] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('search');

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (savedHistory) {
        const parsed = JSON.parse(savedHistory);
        const historyWithDates = parsed.map(item => ({
          ...item,
          timestamp: new Date(item.timestamp)
        }));
        setSearchHistory(historyWithDates);
      }
    } catch (error) {
      console.error('Error loading search history:', error);
    }
  }, []);

  // Save history to localStorage whenever it changes
  useEffect(() => {
    if (searchHistory.length > 0) {
      try {
        localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(searchHistory));
      } catch (error) {
        console.error('Error saving search history:', error);
      }
    }
  }, [searchHistory]);

  const examplePrompts = [
    "Find football strikers in Manchester under 25 years old",
    "Show me basketball coaches with experience",
    "Looking for nutritionists who work with athletes"
  ];

  const handleAISearch = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a search prompt');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Call AI discovery endpoint
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/discover`,
        { prompt },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { users, explanation, searchCriteria } = response.data;
      
      // Set current results
      setCurrentResults(users);
      setCurrentExplanation(explanation);
      
      // Add to search history
      const historyItem = {
        id: Date.now(),
        prompt: prompt.trim(),
        timestamp: new Date(),
        results: users,
        explanation,
        searchCriteria
      };
      
      setSearchHistory(prev => [historyItem, ...prev]);

      toast.success(`Found ${users.length} matching profiles`);
      
      // Clear prompt for next search
      setPrompt('');
      
    } catch (error) {
      console.error('AI Discovery error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process AI search');
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example) => {
    setPrompt(example);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAISearch();
    }
  };

  const deleteHistoryItem = (itemId, e) => {
    e.stopPropagation();
    setSearchHistory(prev => prev.filter(item => item.id !== itemId));
    toast.success('History item deleted');
  };

  const clearAllHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem(HISTORY_STORAGE_KEY);
    toast.success('All history cleared');
  };

  const loadHistorySearch = (historyItem) => {
    setCurrentResults(historyItem.results);
    setCurrentExplanation(historyItem.explanation);
    setActiveTab('search');
  };

  const formatTimestamp = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const ProfileCard = ({ user }) => {
    const handleProfileClick = () => {
      navigate(`/profile/${user.username}`);
    };

    // Helper to get stats display based on persona
    const getStatsDisplay = () => {
      if (!user.stats) return null;
      
      switch(user.persona) {
        case 'athlete':
          if (user.stats.competitions || user.stats.wins || user.stats.podiums) {
            return (
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                {user.stats.competitions && <span>🏆 {user.stats.competitions} competitions</span>}
                {user.stats.wins && <span>🥇 {user.stats.wins} wins</span>}
                {user.stats.podiums && <span>🥉 {user.stats.podiums} podiums</span>}
              </div>
            );
          }
          break;
        case 'coach':
          if (user.stats.yearsExperience || user.stats.athletesTrained || user.stats.championships) {
            return (
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                {user.stats.yearsExperience && <span>📅 {user.stats.yearsExperience} years exp</span>}
                {user.stats.athletesTrained && <span>👥 {user.stats.athletesTrained} athletes</span>}
                {user.stats.championships && <span>🏆 {user.stats.championships} championships</span>}
              </div>
            );
          }
          break;
        case 'club':
          if (user.stats.founded || user.stats.members || user.stats.trophies) {
            return (
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                {user.stats.founded && <span>📅 Est. {user.stats.founded}</span>}
                {user.stats.members && <span>👥 {user.stats.members} members</span>}
                {user.stats.trophies && <span>🏆 {user.stats.trophies} trophies</span>}
              </div>
            );
          }
          break;
        case 'nutritionist':
        case 'physio':
          if (user.stats.yearsExperience || user.stats.clientsHelped || user.stats.athletesTreated) {
            return (
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                {user.stats.yearsExperience && <span>📅 {user.stats.yearsExperience} years exp</span>}
                {(user.stats.clientsHelped || user.stats.athletesTreated) && <span>👥 {user.stats.clientsHelped || user.stats.athletesTreated} clients</span>}
              </div>
            );
          }
          break;
        default:
          return null;
      }
      return null;
    };

    return (
      <Card
        className="cursor-pointer hover:shadow-lg transition-all"
        onClick={handleProfileClick}
      >
        <CardContent className="p-4">
          <div className="flex items-center space-x-3 mb-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={user.profileImage} alt={user.name} />
              <AvatarFallback>{user.name?.[0] || 'U'}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground truncate">{user.name}</p>
              <p className="text-sm text-muted-foreground truncate">@{user.username}</p>
            </div>
            <Badge variant="secondary" className="capitalize">
              {user.persona}
            </Badge>
          </div>
          
          <div className="space-y-2 text-sm">
            {user.sports && user.sports.length > 0 && (
              <div className="flex items-center text-muted-foreground">
                <Trophy className="w-4 h-4 mr-2 text-primary" />
                {user.sports.join(', ')}
              </div>
            )}
            {user.location && (
              <div className="flex items-center text-muted-foreground">
                <MapPin className="w-4 h-4 mr-2 text-primary" />
                {user.location}
              </div>
            )}
            {getStatsDisplay()}
            {user.bio && (
              <p className="text-muted-foreground line-clamp-2 mt-2">
                {user.bio}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-20">
        <div className="py-6">
          {/* Header */}
          <div className="flex flex-col items-center text-center mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2 bg-primary">
              <Radar className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-foreground">iDiscover</h1>
            <p className="text-muted-foreground text-sm">
              Find Talent Using Your Natural Language
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="search" className="flex items-center">
                <SearchIcon className="w-4 h-4 mr-2" />
                Search
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center">
                <History className="w-4 h-4 mr-2" />
                History {searchHistory.length > 0 && `(${searchHistory.length})`}
              </TabsTrigger>
            </TabsList>

            {/* Search Tab */}
            <TabsContent value="search" className="space-y-6">
              {/* Search Input */}
              <Card className="rounded-2xl border-2 border-primary/30 shadow-xl ring-2 ring-primary/10">
                <CardContent className="p-2">
                  <div className="flex items-center gap-2">
                    <Textarea
                      placeholder="Describe who you're looking for... (e.g., 'Find football strikers in Manchester under 25')"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="min-h-[60px] md:min-h-[65px] resize-none rounded-xl border-2 border-primary/20 focus:border-primary focus-visible:ring-2 focus-visible:ring-primary/30 bg-muted/30 text-sm transition-all py-3 px-4 flex-1"
                    />
                    <button
                      onClick={handleAISearch}
                      disabled={loading || !prompt.trim()}
                      className="h-10 w-10 md:h-12 md:w-12 rounded-xl transition-all duration-300 flex-shrink-0 shadow-lg hover:shadow-xl hover:scale-105 hover:opacity-90 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                      style={{ backgroundColor: 'hsl(271, 76%, 53%)' }}
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin text-white" />
                      ) : (
                        <Send className="w-4 h-4 md:w-5 md:h-5 text-white" />
                      )}
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Example Prompts - Show only if no results */}
              {!currentResults && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Examples:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {examplePrompts.map((example, index) => (
                      <Card
                        key={index}
                        className="cursor-pointer hover:shadow-md transition-all rounded-xl"
                        onClick={() => handleExampleClick(example)}
                      >
                        <CardContent className="p-3">
                          <p className="text-sm text-foreground">{example}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Results */}
              {currentResults && (
                <div className="space-y-4">
                  {/* AI Explanation */}
                  <Card className="bg-secondary">
                    <CardContent className="p-4 flex items-start space-x-3">
                      <Sparkles className="w-5 h-5 mt-1 text-primary" />
                      <p className="text-foreground">{currentExplanation}</p>
                    </CardContent>
                  </Card>

                  {/* Results Grid */}
                  {currentResults.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {currentResults.map((user) => (
                        <ProfileCard key={user._id} user={user} />
                      ))}
                    </div>
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-lg font-semibold text-foreground mb-2">No profiles found</p>
                        <p className="text-muted-foreground">Try adjusting your search criteria</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history">
              {searchHistory.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <History className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-lg font-semibold text-foreground mb-2">No search history</p>
                    <p className="text-muted-foreground">Your AI searches will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Clear All Button */}
                  <div className="flex justify-end mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAllHistory}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      Clear All History
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {searchHistory.map((item) => (
                      <Card
                        key={item.id}
                        className="cursor-pointer hover:shadow-md transition-all"
                        onClick={() => loadHistorySearch(item)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <p className="font-semibold text-foreground mb-1">{item.prompt}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatTimestamp(item.timestamp)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">
                                {item.results.length} results
                              </Badge>
                              <button
                                onClick={(e) => deleteHistoryItem(item.id, e)}
                                className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-full transition-colors"
                                title="Delete this search"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          {item.explanation && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {item.explanation}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

    </div>
  );
};

export default AIDiscover;
